import React, { ReactNode } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

// Create a client
const makeQueryClient = () => new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes
      retry: (failureCount, error: Error & { status?: number; code?: number }) => {
        // Don't retry on 404s or auth errors
        const status = error?.status || error?.code;
        if (status === 404 || status === 401 || status === 403) {
          return false;
        }
        return failureCount < 3;
      },
      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 30000),
    },
    mutations: {
      retry: (failureCount, error: Error & { status?: number; code?: number }) => {
        // Don't retry on 404s or auth errors
        const status = error?.status || error?.code;
        if (status === 404 || status === 401 || status === 403) {
          return false;
        }
        return failureCount < 2;
      },
    },
  },
});

interface QueryProviderProps {
  children: ReactNode;
}

export function QueryProvider({ children }: QueryProviderProps) {
  const [queryClient] = React.useState(makeQueryClient);
  return (
    <QueryClientProvider client={queryClient}>
      {children}
      {/* Add DevTools only in development and only if available as a dynamic import */}
      {process.env.NODE_ENV === "development" && process.env.NEXT_PUBLIC_ENABLE_DEBUG_TOOLS === "true" &&
        (() => {
          try {
            // Using dynamic import for the DevTools
            const ReactQueryDevtools = React.lazy(() =>
              import("@tanstack/react-query-devtools").then((module) => ({
                default: module.ReactQueryDevtools,
              }))
            );

            return (
              <React.Suspense fallback={null}>
                <ReactQueryDevtools initialIsOpen={false} />
              </React.Suspense>
            );
          } catch (e) {
            console.warn("React Query DevTools not available:", e);
            return null;
          }
        })()}
    </QueryClientProvider>
  );
}
